import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export function YearlyPublicationsChart({
  data: externalData = null,
  loading: externalLoading = false,
  error: externalError = null,
  journalId = null,
}) {
  // API에서 데이터 가져오기 (externalData가 없을 때만)
  const [publicationsData, setPublicationsData] = useState(null);
  const [publicationsLoading, setPublicationsLoading] = useState(false);
  const [publicationsErr, setPublicationsErr] = useState(null);

  useEffect(() => {
    // externalData가 제공되면 API 호출하지 않음
    if (externalData !== null) {
      return;
    }

    // journalId가 없으면 API 호출하지 않음
    if (!journalId) {
      return;
    }

    let alive = true;
    async function fetchPublications() {
      setPublicationsLoading(true);
      setPublicationsErr(null);
      try {
        const response = await get(`/rm/v1/journal/${encodeURIComponent(journalId)}/yearly_publications`);
        if (alive) {
          if (response?.status && response?.result) {
            setPublicationsData(response.result);
          } else {
            setPublicationsErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setPublicationsData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setPublicationsErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setPublicationsLoading(false);
        }
      }
    }
    fetchPublications();
    return () => { alive = false; };
  }, [externalData, journalId]);

  // 데이터 처리: externalData 또는 API 데이터 사용
  const data = externalData || publicationsData || [];
  const ref = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';

    const width = el.clientWidth || 320;
    // slightly shorter height to match design
    const height = el.clientHeight || 220;
    const margin = { top: 10, right: 16, bottom: 40, left: 44 };

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleBand()
      .domain(data.map(d => d.year))
      .range([0, innerW])
      // a bit more padding to make bars slightly thinner
      .padding(0.35);

    const maxY = (d3.max(data, d => d.value) || 0) * 1.1 || 10;
    const y = d3.scaleLinear().domain([0, maxY]).range([innerH, 0]).nice();

    // Y axis with horizontal gridlines
    g.append('g')
      .attr('transform', `translate(0,0)`)
      .call(
        d3
          .axisLeft(y)
          .ticks(7)
          .tickSize(-innerW)
          .tickFormat(d => `${d}`)
      )
      .call(g => {
        g.selectAll('.domain').remove();
        g.selectAll('line').attr('stroke', '#E4E8F1');
        g.selectAll('text').attr('fill', '#8D9AAF').style('font-size', 13);
      });

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).tickSize(0))
      .call(g => {
        g.selectAll('.domain').remove();
        g.selectAll('text').attr('fill', '#6C7687').style('font-size', 13).style('transform', 'translateY(10px)');
      });

    // Bars: make about 80% of bandwidth and centered
    const barWidthFactor = 0.8;
    const bars = g
      .selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => (x(d.year) ?? 0) + (x.bandwidth() * (1 - barWidthFactor)) / 2)
      .attr('y', innerH)
      .attr('width', x.bandwidth() * barWidthFactor)
      .attr('height', 0)
      .attr('rx', 3)
      .attr('fill', '#4F55FF')
      .attr('opacity', 0.95)
      .transition()
      .duration(600)
      .attr('y', d => y(d.value))
      .attr('height', d => innerH - y(d.value));

    // Tooltip interaction
    const tooltip = d3.select(tooltipRef.current);
    g.selectAll('.hit-area')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'hit-area')
      .attr('x', d => x(d.year))
      .attr('y', 0)
      .attr('width', x.bandwidth())
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .on('mousemove', function (event, d) {
        const [mx, my] = d3.pointer(event, this);
        tooltip
          .style('display', 'block')
          .style('left', `${margin.left + (x(d.year) || 0) + x.bandwidth() / 2 - 40}px`)
          .style('top', `${margin.top + my - 36}px`)
          .html(`<div class='px-2 py-1 text-[13px]'><span class='font-semibold text-[#2A2E36] mr-2'>${d.year}</span><span class='text-[#2A2E36]'>${d.value}</span></div>`);
      })
      .on('mouseleave', () => tooltip.style('display', 'none'));
  }, [data]);

  // 데이터가 없을 때 처리
  const hasData = Array.isArray(data) && data.length > 0;
  const showLoading = !!externalLoading || (externalData === null && publicationsLoading);
  const showErr = externalError || (externalData === null ? publicationsErr : null);

  return (
    <div className="relative min-h-[220px]">
      {hasData && (
        <>
          <div ref={ref} className="w-full" />
          <div
            ref={tooltipRef}
            style={{ display: 'none' }}
            className="pointer-events-none absolute z-10 rounded bg-white border border-[#E4E8F1] shadow px-2 py-1 text-[12px] text-[#2A2E36]"
          />
        </>
      )}
      {/* 로딩 상태 오버레이 */}
      <ChartSkeletonLoader show={showLoading} />
      {/* 에러 상태 오버레이 */}
      {showErr && !showLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-[#F3652D] text-sm">
          {showErr}
        </div>
      )}
      {/* 데이터 없음 오버레이 */}
      {!hasData && !showLoading && !showErr && (
        <div className="absolute inset-0 flex items-center justify-center text-[#8D9AAF]">
          데이터가 없습니다.
        </div>
      )}
    </div>
  );
}
